import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";
import ActivityScatterChart from "./ActivityScatterChart";

import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  TableSortLabel,
  Skeleton,
  Typography,
} from "@mui/material";

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const TableSkeleton = ({ rows = 6 }) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Team Name</TableCell>
          <TableCell>Charts Changed (60 days)</TableCell>
          <TableCell>MRR</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton width={180} /></TableCell>
            <TableCell><Skeleton width={120} /></TableCell>
            <TableCell><Skeleton width={80} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const Activity = () => {
  const [activity, setActivity] = useState([]);
  const [orderBy, setOrderBy] = useState("charts_changed_last_60_days");
  const [order, setOrder] = useState("desc");
  const [tabIndex, setTabIndex] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/api/team/activity`);
        if (Array.isArray(response.data)) {
          setActivity(response.data);
        } else {
          console.error("Expected array, got:", response.data);
          setError("Unexpected response format.");
        }
      } catch (err) {
        console.error("Error fetching activity data", err);
        setError("Failed to load activity data.");
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, []);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const groups = useMemo(() => {
    const g = {
      highMRRLowActivity: [],
      highMRRHighActivity: [],
      lowMRRLowActivity: [],
      lowMRRHighActivity: [],
    };
    activity.forEach((row) => {
      const mrr = row.mrr ?? 0;
      const act = row.charts_changed_last_60_days ?? 0;

      if (mrr > 65 && act < 3) g.highMRRLowActivity.push(row);
      else if (mrr > 65 && act >= 3) g.highMRRHighActivity.push(row);
      else if (mrr <= 65 && act < 3) g.lowMRRLowActivity.push(row);
      else if (mrr <= 65 && act >= 3) g.lowMRRHighActivity.push(row);
    });
    return g;
  }, [activity]);

  const renderGroupTable = (rows) => {
    const totals = rows.reduce(
      (acc, r) => {
        acc.count += 1;
        acc.charts += Number(r?.charts_changed_last_60_days ?? 0);
        acc.mrr += typeof r?.mrr === "number" ? r.mrr : 0;
        return acc;
      },
      { count: 0, charts: 0, mrr: 0 }
    );
  
    const sorted = [...rows].sort((a, b) => {
      const valA = a[orderBy];
      const valB = b[orderBy];
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (typeof valA === "number")
        return order === "asc" ? valA - valB : valB - valA;
      return order === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "team_name"}
                  direction={orderBy === "team_name" ? order : "asc"}
                  onClick={() => handleSort("team_name")}
                >
                  Team Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "charts_changed_last_60_days"}
                  direction={
                    orderBy === "charts_changed_last_60_days" ? order : "asc"
                  }
                  onClick={() => handleSort("charts_changed_last_60_days")}
                >
                  Charts Changed (60 days)
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "mrr"}
                  direction={orderBy === "mrr" ? order : "asc"}
                  onClick={() => handleSort("mrr")}
                >
                  MRR
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
  
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.team_id}>
                <TableCell>
                  <a href={`/team/${row.team_id}`}>{row.team_name}</a>
                </TableCell>
                <TableCell>{row.charts_changed_last_60_days ?? 0}</TableCell>
                <TableCell>
                  {typeof row.mrr === "number" ? `$${row.mrr.toFixed(2)}` : "N/A"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
  
          <TableFooter>
            <TableRow
            sx={{
              backgroundColor: "#f0f4ff", // light blue-gray
            }}
            >
              <TableCell sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                Total ({totals.count} teams)
              </TableCell>
              <TableCell sx={{ fontWeight: "bold",fontSize: "0.9rem"}}>{totals.charts}</TableCell>
              <TableCell sx={{ fontWeight: "bold",fontSize: "0.9rem" }}>
                ${totals.mrr.toFixed(2)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  };
  

  const {
    highMRRLowActivity,
    highMRRHighActivity,
    lowMRRLowActivity,
    lowMRRHighActivity,
  } = groups;

  const tabLabels = [
    `📉 High MRR & Low Activity (Churn Risk) (${highMRRLowActivity.length})`,
    `🚀 High MRR & High Activity (Upsell) (${highMRRHighActivity.length})`,
    `⚠️ Low MRR & Low Activity (Churn Risk) (${lowMRRLowActivity.length})`,
    `📈 Low MRR & High Activity (Upsell) (${lowMRRHighActivity.length})`,
  ];

  const tabData = [
    highMRRLowActivity,
    highMRRHighActivity,
    lowMRRLowActivity,
    lowMRRHighActivity,
  ];

  return (
    <div>
      <h2>Customer Activity (Last 60 Days)</h2>

      {/* Chart area */}
      {loading ? (
        <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
      ) : error ? (
        <Box sx={{ my: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : activity.length === 0 ? (
        <Box sx={{ my: 2 }}>
          <Typography>No activity found</Typography>
        </Box>
      ) : (
        <ActivityScatterChart data={activity} />
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 4 }}>
        {loading ? (
          <Box sx={{ display: "flex", gap: 2, py: 1 }}>
            <Skeleton variant="rounded" width={260} height={36} />
            <Skeleton variant="rounded" width={260} height={36} />
            <Skeleton variant="rounded" width={260} height={36} />
            <Skeleton variant="rounded" width={260} height={36} />
          </Box>
        ) : (
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabLabels.map((label, index) => (
              <Tab
                label={label}
                key={index}
                sx={{
                  textTransform: "none",
                  "&:hover": {
                    color: "#0070f3",
                    backgroundColor: "#f5f5f5",
                  },
                  "&.Mui-selected": {
                    color: "#0070f3",
                  },
                }}
              />
            ))}
          </Tabs>
        )}
      </Box>

      {/* Tables per tab */}
      {loading ? (
        <Box sx={{ mt: 2 }}>
          <TableSkeleton />
        </Box>
      ) : error ? null : activity.length === 0 ? null : (
        tabData.map((group, index) => (
          <TabPanel value={tabIndex} index={index} key={index}>
            {group.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No teams in this segment.
                </Typography>
              </Box>
            ) : (
              renderGroupTable(group)
            )}
          </TabPanel>
        ))
      )}

      {/* Optional: small global spinner if you prefer instead of skeletons */}
      {/* {loading && (
        <Box sx={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <CircularProgress />
        </Box>
      )} */}
    </div>
  );
};

export default Activity;
